import {
  MENU_ICON_SLOT_CLASSES,
  MENU_ITEM_CLASSES,
  MENU_PANEL_CLASSES,
  MENU_SEPARATOR_CLASSES,
  useDrillFocus,
} from "@controls";
import { ChevronRight, CircleXmarkRegular, PlusRegular } from "@icons";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import BooleanPanel from "../menu/boolean-panel";
import ComparisonPanel from "../menu/comparison-panel";
import { DATE_OPERATOR_KEYS, NUMERIC_OPERATOR_KEYS } from "../menu/constants";
import TextOptionsPanel from "../menu/text-options-panel";
import {
  ComparisonFilterValue,
  ComparisonOperator,
  CompoundCondition,
  FilterMenuItem,
  TextOperator,
} from "../menu/types";
import {
  DATE_OPERATOR_LABEL_KEYS,
  NUMERIC_OPERATOR_LABEL_KEYS,
  TEXT_OPERATOR_LABEL_KEYS,
} from "./constants";

function getConditionOperatorLabel(
  condition: CompoundCondition,
  t: (key: string) => string,
): string {
  switch (condition.type) {
    case "TEXT":
      return t(TEXT_OPERATOR_LABEL_KEYS[condition.operator]);
    case "DATE":
      return t(
        DATE_OPERATOR_LABEL_KEYS[condition.operator as ComparisonOperator],
      ).toLowerCase();
    case "NUMERIC":
      return t(
        NUMERIC_OPERATOR_LABEL_KEYS[condition.operator as ComparisonOperator],
      ).toLowerCase();
    case "BOOLEAN":
      return t("textOperatorIs");
  }
}

function getConditionValueLabel(
  condition: CompoundCondition,
  t: (key: string) => string,
  menuItems: FilterMenuItem[],
): string {
  switch (condition.type) {
    case "TEXT": {
      const menuItem = menuItems.find((m) => m.name === condition.fieldName);
      if (menuItem?.type === "TEXT") {
        const optionsMap = new Map(
          menuItem.options.map((o) => [o.value, o.label]),
        );
        return condition.value
          .map((v) => {
            const label = optionsMap.get(v);
            return typeof label === "string" ? label : v;
          })
          .join(", ");
      }
      return condition.value.join(", ");
    }
    case "BOOLEAN":
      return t(condition.value === "1" ? "booleanYes" : "booleanNo");
    default:
      return condition.value;
  }
}

function buildCondition(
  menuItem: FilterMenuItem,
  operator: string,
  value: string | string[],
): CompoundCondition {
  switch (menuItem.type) {
    case "TEXT":
      return {
        fieldName: menuItem.name,
        label: menuItem.label,
        type: "TEXT",
        operator: operator as TextOperator,
        value: value as string[],
      };
    case "DATE":
      return {
        fieldName: menuItem.name,
        label: menuItem.label,
        type: "DATE",
        operator: operator as ComparisonOperator,
        value: value as string,
      };
    case "NUMERIC":
      return {
        fieldName: menuItem.name,
        label: menuItem.label,
        type: "NUMERIC",
        operator: operator as ComparisonOperator,
        value: value as string,
      };
    case "BOOLEAN":
      return {
        fieldName: menuItem.name,
        label: menuItem.label,
        type: "BOOLEAN",
        operator: "eq",
        value: value as string,
      };
  }
}

export default function ConditionSlot({
  condition,
  filterMenuItemGroups,
  onSetCondition,
  onRemove,
}: {
  condition: CompoundCondition | null;
  filterMenuItemGroups: FilterMenuItem[][];
  onSetCondition: (condition: CompoundCondition) => void;
  onRemove?: () => void;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [activeMenu, setActiveMenu] = useState<FilterMenuItem | null>(null);
  const contentRef = useDrillFocus(activeMenu ? [activeMenu.name] : [], open);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const isTriggerReplaced = useRef(false);

  const allMenuItems = filterMenuItemGroups.flat();

  function handleOpenChange(nextOpen: boolean) {
    setOpen(nextOpen);
    if (nextOpen) {
      isTriggerReplaced.current = false;
    }
    if (nextOpen && condition) {
      const match = allMenuItems.find((m) => m.name === condition.fieldName);
      if (match) {
        setActiveMenu(match);
        return;
      }
    }
    if (!nextOpen) {
      setActiveMenu(null);
    }
  }

  function handleConditionUpdate(cond: CompoundCondition) {
    onSetCondition(cond);
  }

  function handleConditionComplete(cond: CompoundCondition) {
    isTriggerReplaced.current = condition === null;
    onSetCondition(cond);
    setOpen(false);
    setActiveMenu(null);
  }

  useEffect(() => {
    if (!open && condition && isTriggerReplaced.current) {
      isTriggerReplaced.current = false;
      triggerRef.current?.focus();
    }
  }, [condition, open]);

  const isEditingSameField =
    condition && activeMenu && condition.fieldName === activeMenu.name;

  function renderPanel() {
    if (!activeMenu) return null;

    if (activeMenu.type === "TEXT") {
      const currentSelected =
        isEditingSameField && condition.type === "TEXT" ? condition.value : [];

      return (
        <TextOptionsPanel
          menuItem={activeMenu}
          selected={currentSelected}
          onSelectionChange={(values) => {
            if (values.length === 0) return;
            handleConditionUpdate(
              buildCondition(
                activeMenu,
                values.length > 1 ? "in" : "eq",
                values,
              ),
            );
          }}
          onBack={() => setActiveMenu(null)}
        />
      );
    }

    if (activeMenu.type === "BOOLEAN") {
      const currentValue =
        isEditingSameField && condition.type === "BOOLEAN"
          ? condition.value
          : undefined;

      return (
        <BooleanPanel
          menuItem={activeMenu}
          value={currentValue}
          onChange={(value) =>
            handleConditionComplete(buildCondition(activeMenu, "eq", value))
          }
          onBack={() => setActiveMenu(null)}
        />
      );
    }

    const currentComparisonValue =
      isEditingSameField &&
      (condition.type === "DATE" || condition.type === "NUMERIC")
        ? { operator: condition.operator, value: condition.value }
        : undefined;

    if (activeMenu.type === "DATE") {
      return (
        <ComparisonPanel
          menuItem={activeMenu}
          inputType="date"
          operatorKeys={DATE_OPERATOR_KEYS}
          value={currentComparisonValue}
          onChange={(value: ComparisonFilterValue) =>
            handleConditionUpdate(
              buildCondition(activeMenu, value.operator, value.value),
            )
          }
          onBack={() => setActiveMenu(null)}
        />
      );
    }

    return (
      <ComparisonPanel
        menuItem={activeMenu}
        inputType="number"
        operatorKeys={NUMERIC_OPERATOR_KEYS}
        value={currentComparisonValue}
        onChange={(value: ComparisonFilterValue) =>
          handleConditionUpdate(
            buildCondition(activeMenu, value.operator, value.value),
          )
        }
        onBack={() => setActiveMenu(null)}
      />
    );
  }

  const trigger = condition ? (
    <div className="flex items-center gap-1 rounded-md border border-divider bg-page px-2 py-1 text-xs text-default">
      <DropdownMenu.Trigger asChild>
        <button
          ref={triggerRef}
          type="button"
          className="flex items-center gap-1 cursor-pointer hover:text-default"
        >
          <span className="font-medium">{condition.label}</span>
          <span className="text-muted">
            {getConditionOperatorLabel(condition, t)}
          </span>
          <span className="max-w-24 truncate">
            {getConditionValueLabel(condition, t, allMenuItems)}
          </span>
        </button>
      </DropdownMenu.Trigger>
      {onRemove && (
        <button
          type="button"
          onClick={onRemove}
          className="ml-0.5 flex items-center text-muted hover:text-muted"
          aria-label={t("removeCondition")}
        >
          <CircleXmarkRegular />
        </button>
      )}
    </div>
  ) : (
    <DropdownMenu.Trigger asChild>
      <button
        ref={triggerRef}
        type="button"
        className="flex items-center gap-1 rounded-md border border-dashed border-default px-2 py-1 text-xs text-muted hover:border-default hover:text-muted"
      >
        <PlusRegular />
        <span>{t("filterTable")}</span>
      </button>
    </DropdownMenu.Trigger>
  );

  return (
    <DropdownMenu.Root
      open={open}
      onOpenChange={handleOpenChange}
      modal={false}
    >
      {trigger}
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          ref={contentRef}
          className={`${MENU_PANEL_CLASSES} min-w-64`}
          sideOffset={6}
          align="start"
          side="bottom"
        >
          {activeMenu ? (
            renderPanel()
          ) : (
            <div>
              {filterMenuItemGroups.map((group, groupIndex) => (
                <DropdownMenu.Group key={groupIndex}>
                  {groupIndex > 0 && (
                    <DropdownMenu.Separator
                      className={MENU_SEPARATOR_CLASSES}
                    />
                  )}
                  {group.map((menuItem) => (
                    <DropdownMenu.Item
                      key={menuItem.name}
                      data-menu-key={menuItem.name}
                      onSelect={(e) => {
                        e.preventDefault();
                        setActiveMenu(menuItem);
                      }}
                      className={MENU_ITEM_CLASSES}
                    >
                      <span className={MENU_ICON_SLOT_CLASSES}>
                        {menuItem.icon}
                      </span>
                      <span className="flex-1 min-w-0">{menuItem.label}</span>
                      <span className="ml-auto shrink-0 text-muted transition-colors duration-100 group-data-[highlighted]:text-primary">
                        <ChevronRight size={16} />
                      </span>
                    </DropdownMenu.Item>
                  ))}
                </DropdownMenu.Group>
              ))}
            </div>
          )}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}
